import { findKey } from 'lodash-es';

import { Subject, SupportedSubjects } from 'types/Subject';

import { Id } from '../../../types/Id';

type nameFormat = 'full' | 'short';

const _subjectsCodes: Record<SupportedSubjects, number> = {
  CH: 1,
  M: 2,
  MR: 3,
  B: 4,
  FI: 5,
  GE: 6,
  HI: 7,
  JA: 8,
  PP: 11,
};

const _subjectsNames: Record<SupportedSubjects, string> = {
  GE: 'Geografia',
  FI: 'Fizyka',
  CH: 'Chemia',
  MR: 'Matematyka rozszerzona',
  JA: 'Język Angielski',
  HI: 'Historia',
  M: 'Matematyka podstawowa',
  B: 'Biologia',
  PP: 'Język polski podstawowy',
};

const _subjectsShortNames: Record<SupportedSubjects, string> = {
  GE: 'GE',
  FI: 'FI',
  CH: 'CH',
  MR: 'MR',
  JA: 'JA',
  HI: 'HI',
  M: 'MP',
  B: 'BI',
  PP: 'PP',
};

const parseSubjectCodeToSubjectNumber = (subjectCode: SupportedSubjects): number => _subjectsCodes[subjectCode];

const parseSubjectNumberToSubjectCode = (subjectNumber: number | Id): SupportedSubjects =>
  <SupportedSubjects>findKey(_subjectsCodes, v => v === subjectNumber);

const resolveSubjectNameFromSubjectCode = (subjectString: SupportedSubjects, format: nameFormat): string => {
  switch (format) {
    case 'short': {
      return _subjectsShortNames[subjectString];
    }
    case 'full':
    default: {
      return _subjectsNames[subjectString];
    }
  }
};

const resolveSubjectNameFromSubjectNumber = (subjectNumber: number, format: nameFormat): string =>
  resolveSubjectNameFromSubjectCode(parseSubjectNumberToSubjectCode(subjectNumber), format);

/** Prefer numeric `pk`; fall back to subjectCode map or a numeric `id` string (legacy Relay ids return null). */
const resolveSubjectNumericId = (subject: Pick<Subject, 'pk' | 'id' | 'subjectCode'> | null | undefined): number | null => {
  if (!subject) return null;
  if (typeof subject.pk === 'number' && !Number.isNaN(subject.pk)) return subject.pk;
  if (subject.subjectCode) {
    const fromCode = parseSubjectCodeToSubjectNumber(subject.subjectCode);
    if (typeof fromCode === 'number' && !Number.isNaN(fromCode)) return fromCode;
  }
  const fromId = Number(subject.id);
  if (!Number.isNaN(fromId)) return fromId;
  return null;
};

export {
  parseSubjectCodeToSubjectNumber,
  parseSubjectNumberToSubjectCode,
  resolveSubjectNameFromSubjectCode,
  resolveSubjectNameFromSubjectNumber,
  resolveSubjectNumericId,
};
